import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { UploadErrorPopup } from './UploadErrorPopup.tsx';

export const i18n: {
	closeUploadErrorPopupButton: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	uploadErrorPopupTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	uploadErrorPopupContent: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bulkUploadErrorPopupTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bulkUploadErrorPopupContent: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	tryDifferentDocumentUploadErrorPopupButton: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	tryDifferentDocumentsUploadErrorPopupButton: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	failedBulkImportPopupTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	failedBulkImportPopupContent: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	closeUploadErrorPopupButton: {
		id: 'import-from-sources.upload-error-popup.close',
		defaultMessage: 'Close',
		description: 'Label for the button that closes the upload error popup modal.',
	},
	uploadErrorPopupTitle: {
		id: 'import-from-sources.upload-error-popup.title',
		defaultMessage: 'This file can’t be imported',
		description:
			'Title of the error popup shown when a single file fails to import due to type, protection, or read-only issues.',
	},
	uploadErrorPopupContent: {
		id: 'import-from-sources.upload-error-popup.content',
		defaultMessage:
			'We ran into issues because the file may the wrong type, password protected, or read-only.',
		description:
			'Body text of the error popup explaining possible reasons why the file could not be imported (wrong type, password protected, or read-only).',
	},
	bulkUploadErrorPopupTitle: {
		id: 'import-from-sources.bulk-upload-error-popup.title',
		defaultMessage: 'These files can’t be imported',
		description:
			'Title of the error popup shown when multiple files fail to import due to type, protection, or read-only issues.',
	},
	bulkUploadErrorPopupContent: {
		id: 'import-from-sources.bulk-upload-error-popup.content',
		defaultMessage:
			'We ran into issues because the files may the wrong type, password protected, or read-only.',
		description:
			'Body text of the error popup explaining possible reasons why multiple files could not be imported (wrong type, password protected, or read-only).',
	},
	tryDifferentDocumentUploadErrorPopupButton: {
		id: 'import-from-sources.upload-error-popup.tryDifferentDocument',
		defaultMessage: 'Select new file',
		description:
			'Label for the button that allows user to select a different file after the previous file failed to import.',
	},
	tryDifferentDocumentsUploadErrorPopupButton: {
		id: 'import-from-sources.upload-error-popup.tryDifferentDocuments',
		defaultMessage: 'Select new files',
		description:
			'Label for the button that allows user to select different files after the previous files failed to import.',
	},
	failedBulkImportPopupTitle: {
		id: 'import-from-sources.upload-error-popup.failedBulkImportPopupTitle',
		defaultMessage: 'Bulk import failed',
		description: 'Title of the error popup shown when a bulk import operation fails completely.',
	},
	failedBulkImportPopupContent: {
		id: 'import-from-sources.upload-error-popup.failedBulkImportPopupContent',
		defaultMessage:
			'We ran into a problem importing your files. Check your internet connection and try again.',
		description:
			'Body text of the error popup explaining that bulk import failed and suggesting the user check their internet connection.',
	},
});

export const CommonErrorModal: React.FC<{
	onClose: () => void;
	onSubmit: () => void;
	headText?: JSX.Element;
	bodyText?: JSX.Element;
	submitText?: JSX.Element;
	modalWidth?: number | string;
	hideCloseAction?: boolean;
	suppressSubmitAnalytics?: boolean;
}> = ({
	onClose,
	onSubmit,
	headText,
	bodyText,
	submitText,
	modalWidth,
	hideCloseAction,
	suppressSubmitAnalytics,
}) => {
	return (
		<UploadErrorPopup
			onClose={onClose}
			onSubmit={onSubmit}
			headText={headText || <FormattedMessage {...i18n.uploadErrorPopupTitle} />}
			bodyText={bodyText || <FormattedMessage {...i18n.uploadErrorPopupContent} />}
			submitText={
				submitText || <FormattedMessage {...i18n.tryDifferentDocumentUploadErrorPopupButton} />
			}
			closeText={<FormattedMessage {...i18n.closeUploadErrorPopupButton} />}
			modalWidth={modalWidth}
			hideCloseAction={hideCloseAction}
			suppressSubmitAnalytics={suppressSubmitAnalytics}
		/>
	);
};
